import { Navigate, Route, Routes } from 'react-router-dom';
import { Button, Card, ErrorBoundary } from '@timely/ui';
import { configProblems } from './config/env';
import { SessionProvider } from './auth/SessionProvider';
import { RequireAuth, RequireVerified } from './auth/RequireAuth';
import { WorkspaceProvider } from './workspace/WorkspaceProvider';
import { RequireWorkspace, WorkspaceHomeRedirect } from './workspace/RequireWorkspace';
import { SignIn } from './screens/SignIn';
import { SignUp } from './screens/SignUp';
import { ForgotPassword, ResetPassword } from './screens/PasswordScreens';
import { AuthCallback, VerifyEmail } from './screens/AuthCallback';
import { AcceptInvite } from './screens/AcceptInvite';
import { CreateCompany } from './screens/CreateCompany';
import { AuthLayout } from './screens/AuthLayout';
import { Today } from './screens/Today';
import { Cheques } from './screens/Cheques';
import { Reports } from './screens/Reports';
import { Stock } from './screens/Stock';
import { Templates } from './screens/Templates';
import { Alerts } from './screens/Alerts';
import { AiSettings } from './screens/AiSettings';
import { CompanyData } from './screens/CompanyData';
import { CompanySetup } from './screens/CompanySetup';
import { Reconciliation } from './screens/Reconciliation';
import { WorkspaceShell } from './workspace/WorkspaceShell';
import { SettingsHome, SettingsLayout } from './workspace/SettingsLayout';
import { MOVED } from './workspace/navigation';
import { Moved } from './workspace/Moved';
import { Team } from './screens/Team';
import { Roles } from './screens/Roles';
import { AuditLog } from './screens/AuditLog';
import { Settings } from './screens/Settings';
import { ReceivablesData } from './screens/ReceivablesData';
import { ConnectGoogle } from './screens/ConnectGoogle';
import { Book } from './screens/Book';
import { accountRoutes } from './screens/CustomerDetail';
import { CustomerStatement } from './screens/CustomerStatement';
import { Account } from './screens/Account';

/**
 * The authenticated product.
 *
 * Real paths, never the hash routing the single-tenant implementation used:
 * invite links, password-reset callbacks and deep links that carry a company
 * all need a path an email client can send and a server can serve.
 *
 * The route tree is the shape of the guarantees. `/login`, `/signup` and the
 * link-landing screens are open because they have to be. `/invite/:token` is
 * open because somebody accepting an invitation may not be signed in yet, and
 * the token is checked in the database, not here. Everything else sits behind
 * a session, and everything tenant-shaped sits behind a resolved membership as
 * well.
 */

/** Shown when the deployment has no backend, instead of a shell that half-works. */
const ConfigurationNeeded = ({ problems }: { problems: ReturnType<typeof configProblems> }) => (
    <main className="max-w-2xl mx-auto px-4 py-16">
        <Card className="px-7 py-8">
            <h1 className="text-title font-semibold text-label tracking-heading">
                Timely Payment is not configured yet
            </h1>
            <p className="text-body text-label-2 mt-3">This build has no backend to talk to. Set the following and reload:</p>
            <ul className="mt-3 space-y-1">
                {problems.map(p => (
                    <li key={p.name + p.detail} className="num text-secondary">
                        <span className="font-semibold text-label">{p.name}</span> {p.detail}
                    </li>
                ))}
            </ul>
            <p className="mt-4 text-secondary text-label-3">
                Copy <span className="num">.env.example</span> to <span className="num">.env.local</span> and fill it in.
            </p>
        </Card>
    </main>
);

/** In the signed-out screens' frame, so a stale link still lands somewhere that looks like Timely Payment. */
const NotFound = () => (
    <AuthLayout title="That page does not exist" lede="The link may be out of date, or the company may have been renamed.">
        <Button variant="secondary" size="lg" onClick={() => { window.location.href = '/'; }}>Go to sign in</Button>
    </AuthLayout>
);

export default function App() {
    const problems = configProblems();
    if (problems.length) return <ConfigurationNeeded problems={problems} />;

    return (
        <ErrorBoundary
            onError={(error, info, reference) => {
                // Session 13 points this at the server log. The reference is
                // what ties a person's report to the entry; the error text is
                // never rendered, because a stack can carry a customer's data.
                console.error(`[${reference}]`, error, info.componentStack);
            }}
        >
            <SessionProvider>
                <WorkspaceProvider>
                    <Routes>
                        {/* Open: reachable without a session, by necessity. */}
                        <Route path="/login" element={<SignIn />} />
                        <Route path="/signup" element={<SignUp />} />
                        <Route path="/forgot-password" element={<ForgotPassword />} />
                        <Route path="/reset-password" element={<ResetPassword />} />
                        <Route path="/auth/callback" element={<AuthCallback />} />
                        {/* The token is verified in the database against a stored
                            digest, and is bound to one address. */}
                        <Route path="/invite/:token" element={<AcceptInvite />} />

                        {/* Signed in, but the address may not be proved yet. */}
                        <Route element={<RequireAuth />}>
                            <Route path="/verify-email" element={<VerifyEmail />} />

                            {/* Verified. Creating a company and accepting an
                                invitation both require it in the database too. */}
                            <Route element={<RequireVerified />}>
                                <Route path="/" element={<WorkspaceHomeRedirect />} />
                                <Route path="/onboarding/company" element={<CreateCompany />} />
                                {/* Where Google returns after consent. Signed in and verified,
                                    because the finish is posted with this person's session. */}
                                <Route path="/connect/google" element={<ConnectGoogle />} />

                                {/* Everything below resolves the slug to a
                                    company this person is in, or refuses. The
                                    shell inside it builds its navigation from
                                    capabilities; the policies and the server
                                    routes refuse the same person if they type
                                    the address instead. */}
                                <Route path="/w/:slug" element={<RequireWorkspace />}>
                                    {/* A document to print: the company's context, without the product's frame around it. */}
                                    <Route path="customers/:customerId/statement" element={<CustomerStatement />} />
                                    <Route element={<WorkspaceShell />}>
                                        {/* The navigation map: docs/product/PRODUCT-UX-FOUNDATION.md §4. */}
                                        <Route index element={<Navigate to="today" replace />} />
                                        <Route path="today" element={<Today />} />
                                        <Route path="customers" element={<Book />} />
                                        {/* The account, Overview first, each tab its own address (Customer 360). */}
                                        {accountRoutes()}
                                        <Route path="cheques" element={<Cheques />} />
                                        <Route path="reports" element={<Reports />} />
                                        <Route path="stock" element={<Stock />} />
                                        <Route path="account" element={<Account />} />
                                        <Route path="settings" element={<SettingsLayout />}>
                                            <Route index element={<SettingsHome />} />
                                            <Route path="company" element={<Settings />} />
                                            <Route path="setup" element={<CompanySetup />} />
                                            <Route path="team" element={<Team />} />
                                            <Route path="roles" element={<Roles />} />
                                            <Route path="ownership" element={<Reconciliation />} />
                                            <Route path="data" element={<ReceivablesData />} />
                                            <Route path="reminders" element={<Alerts />} />
                                            <Route path="templates" element={<Templates />} />
                                            <Route path="ai" element={<AiSettings />} />
                                            <Route path="audit" element={<AuditLog />} />
                                            <Route path="company-data" element={<CompanyData />} />
                                        </Route>
                                        {/* Older addresses keep working. */}
                                        {Object.entries(MOVED).map(([from, to]) => (
                                            <Route key={from} path={`${from}/*`} element={<Moved to={to} />} />
                                        ))}
                                    </Route>
                                </Route>
                            </Route>
                        </Route>

                        <Route path="*" element={<NotFound />} />
                    </Routes>
                </WorkspaceProvider>
            </SessionProvider>
        </ErrorBoundary>
    );
}
